import { Operation, Product, RoleFormModel } from '@user/store/roles/typings'
import { useFormikContext } from 'formik'
import React, { useCallback, useMemo } from 'react'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import { TidalTextFormikField } from '@sales-center/components'
import { CustomSwitch } from './atoms/CustomSwitch'
import { OperationList } from './atoms/OperationList'
import Alert from '@mui/material/Alert'
import { useMapViewFeatureEnabled } from '@user/store/tenantFeatures/hooks/useMapViewFeatureEnabled'
import { useD2DKnockEnabled } from '@user/store/tenantFeatures/hooks/useD2DKnockEnabled'

const OTHER_PERMISSION_SECTION = 'Other'

type Props = {
  products: Product[]
  isAdmin: boolean
  isGuestUser: boolean
}

function getOperationId(operations: Operation[], names: string[]) {
  const operationIds = operations
    .filter((x) => names?.includes(x.name))
    .map((x) => x.id)
  return operationIds
}

export default function CreateUpdateForm(props: Props) {
  const { products, isAdmin, isGuestUser } = props
  const {
    isSubmitting,
    errors,
    setFieldValue,
    submitCount,
    values: { name, permissionOperations },
  } = useFormikContext<RoleFormModel>()
  const disableFields = isSubmitting || isGuestUser
  const mapViewEnabled = useMapViewFeatureEnabled()
  const d2dKnockEnabled = useD2DKnockEnabled()
  // D2D always requires the Map View flag as well, matching the rest of the app.
  const knockStatusEnabled = mapViewEnabled && d2dKnockEnabled

  const product = useMemo(() => products?.[0], [products])

  const permissions = useMemo(
    () =>
      product?.permissions.filter(({ name }) => {
        // The Admin sheet only ever shows Settings; admins bypass every other
        // permission at check time, so 'Other' stays out of their sheet too.
        if (name === OTHER_PERMISSION_SECTION)
          return !isAdmin && knockStatusEnabled
        return isAdmin ? name === 'Settings' : name !== 'Settings'
      }),
    [knockStatusEnabled, isAdmin, product?.permissions],
  )

  // Derived from the *visible* sections so "Enable all permissions" never grants
  // an operation the user cannot see (feature-flagged or Settings-only).
  const flattenedOperations = React.useMemo(
    () =>
      permissions?.reduce<number[]>((prev, cur) => {
        const opIds = cur.operations.map((x) => x.id)
        return [...prev, ...opIds]
      }, []),
    [permissions],
  )

  const isAllSelected = useMemo(
    () =>
      !!flattenedOperations?.length &&
      flattenedOperations.every((id) => permissionOperations?.includes(id)),
    [permissionOperations, flattenedOperations],
  )

  // Only the visible operations are toggled; anything the role already holds in a
  // hidden section is left untouched rather than silently granted or revoked.
  const toggleSelectAll = React.useCallback(() => {
    if (!disableFields) {
      const visible = flattenedOperations ?? []
      const selected = permissionOperations ?? []
      if (isAllSelected) {
        setFieldValue(
          'permissionOperations',
          selected.filter((id) => !visible.includes(id)),
        )
        return
      }
      setFieldValue(
        'permissionOperations',
        Array.from(new Set([...selected, ...visible])),
      )
    }
  }, [
    disableFields,
    flattenedOperations,
    isAllSelected,
    setFieldValue,
    permissionOperations,
  ])

  const onOperationChange = useCallback(
    (
      checked: boolean,
      permissionType: string,
      operationName: string,
      id: number,
    ) => {
      if (disableFields) {
        return
      }
      const tempPermissionOperations = new Set(permissionOperations)
      const permissionIndex = product?.permissions.findIndex(
        (x) => x.name === permissionType,
      )

      if (permissionIndex === -1) return
      const permissionList = product?.permissions[permissionIndex]

      const addOperationDependencies = (operations: string[]) => {
        const ids = getOperationId(permissionList.operations, operations)
        ids.forEach((opId) => tempPermissionOperations.add(opId))
      }

      const removeOperationDependencies = (operations: string[]) => {
        const ids = getOperationId(permissionList.operations, operations)
        ids.forEach((opId) => tempPermissionOperations.delete(opId))
      }

      if (checked) {
        tempPermissionOperations.add(id)
      } else {
        tempPermissionOperations.delete(id)
      }

      switch (operationName) {
        case 'Add':
          if (checked) {
            addOperationDependencies(['View'])
          } else {
            removeOperationDependencies(['Override Automated Assignment'])
          }
          break
        case 'Bulk Update':
          if (checked) addOperationDependencies(['Update', 'View'])
          break
        case 'Bulk Delete':
          if (checked) addOperationDependencies(['Delete', 'View'])
          break
        case 'Update':
          if (!checked)
            removeOperationDependencies([
              'Bulk Update',
              'Cancel Closed/Lost Services',
            ])
          break
        case 'Delete':
          if (!checked) removeOperationDependencies(['Bulk Delete'])
          break
        case 'View':
          if (!checked)
            removeOperationDependencies([
              'Bulk Update',
              'Bulk Delete',
              'Add',
              'Override Automated Assignment',
            ])
          break
        case 'Override Automated Assignment':
          if (checked) addOperationDependencies(['Add'])
          break
        case 'Convert / Save for Later':
          if (!checked) removeOperationDependencies(['Create'])
          break
        case 'Create':
          if (checked) addOperationDependencies(['Convert / Save for Later'])
          break
        case 'Cancel Closed/Lost Services':
          if (checked) addOperationDependencies(['Update'])
          break
        default:
          break
      }

      setFieldValue(
        'permissionOperations',
        Array.from(tempPermissionOperations),
      )
    },
    [disableFields, permissionOperations, product?.permissions, setFieldValue],
  )

  return (
    <Box>
      {!isAdmin && (
        <>
          {isGuestUser && (
            <Box mb={2}>
              <Alert severity="info">
                <Typography variant="body2">
                  {' '}
                  Guest users only have the ability to add leads and will not be
                  able to override assignment rules. Users with this role will
                  not appear in owner dropdowns and will not deduct from your
                  license count.
                </Typography>
              </Alert>
            </Box>
          )}
          <Typography mb={1.6} component="div" variant="h7">
            Role Name
          </Typography>
          <TidalTextFormikField
            name="name"
            label="Role Name"
            required
            disabled={disableFields}
            avoidValidateOnBlur
            inputProps={{
              maxLength: 100,
            }}
            error={errors.name && submitCount > 0 ? true : false}
            helperText={
              errors.name && submitCount > 0
                ? errors.name
                : `${name.length}/100`
            }
          />

          <Typography mb={0.8} mt={3.2} component="div" variant="h7">
            Permissions
          </Typography>

          <Box ml={1.5}>
            <CustomSwitch
              label={'Enable all permissions'}
              disabled={disableFields}
              checked={isAllSelected}
              onChange={toggleSelectAll}
            />
          </Box>
        </>
      )}

      <Box mt={isAdmin ? -3 : 0}>
        {permissions?.map(({ name, operations }, i) => (
          <OperationList
            key={i}
            title={name}
            operations={operations}
            disabled={disableFields}
            onChange={onOperationChange}
          />
        ))}
      </Box>
    </Box>
  )
}
